/**
 * Delivery API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  ArriveDeliveryRequest,
  ConfirmDeliveryRequest,
  CreateDeliveryRequest,
  Delivery,
  DeliveryListParams,
  DeliveryListResponse,
  DeliveryStats,
  ReceiveDeliveryRequest,
  UpdateDeliveryRequest,
} from '../types/delivery';

/**
 * Delivery API Endpoints
 */
export const deliveryApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // =========================================================================
    // LIST
    // =========================================================================

    getDeliveries: builder.query<
      ApiResponse<DeliveryListResponse>,
      { projectId: string; params?: DeliveryListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/deliveries`,
        params,
      }),
      providesTags: ['Delivery'],
    }),

    // =========================================================================
    // STATS
    // =========================================================================

    getDeliveryStats: builder.query<
      ApiResponse<DeliveryStats>,
      { projectId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/deliveries/stats`,
      }),
      providesTags: ['DeliveryStats'],
    }),

    // =========================================================================
    // GET ONE
    // =========================================================================

    getDelivery: builder.query<
      ApiResponse<Delivery>,
      { projectId: string; deliveryId: string }
    >({
      query: ({ projectId, deliveryId }) => ({
        url: `/projects/${projectId}/deliveries/${deliveryId}`,
      }),
      providesTags: (_result, _error, { deliveryId }) => [{ type: 'Delivery', id: deliveryId }],
    }),

    // =========================================================================
    // CREATE
    // =========================================================================

    createDelivery: builder.mutation<
      ApiResponse<Delivery>,
      { projectId: string; data: CreateDeliveryRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/deliveries`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Delivery', 'DeliveryStats'],
    }),

    // =========================================================================
    // UPDATE
    // =========================================================================

    updateDelivery: builder.mutation<
      ApiResponse<Delivery>,
      { projectId: string; deliveryId: string; data: UpdateDeliveryRequest }
    >({
      query: ({ projectId, deliveryId, data }) => ({
        url: `/projects/${projectId}/deliveries/${deliveryId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { deliveryId }) => [
        'Delivery',
        { type: 'Delivery', id: deliveryId },
        'DeliveryStats',
      ],
    }),

    // =========================================================================
    // DELETE
    // =========================================================================

    deleteDelivery: builder.mutation<
      ApiResponse<void>,
      { projectId: string; deliveryId: string }
    >({
      query: ({ projectId, deliveryId }) => ({
        url: `/projects/${projectId}/deliveries/${deliveryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Delivery', 'DeliveryStats'],
    }),

    // =========================================================================
    // WORKFLOW: CONFIRM
    // =========================================================================

    confirmDelivery: builder.mutation<
      ApiResponse<Delivery>,
      { projectId: string; deliveryId: string; data: ConfirmDeliveryRequest }
    >({
      query: ({ projectId, deliveryId, data }) => ({
        url: `/projects/${projectId}/deliveries/${deliveryId}/confirm`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { deliveryId }) => [
        'Delivery',
        { type: 'Delivery', id: deliveryId },
        'DeliveryStats',
      ],
    }),

    // =========================================================================
    // WORKFLOW: IN-TRANSIT
    // =========================================================================

    markDeliveryInTransit: builder.mutation<
      ApiResponse<Delivery>,
      { projectId: string; deliveryId: string }
    >({
      query: ({ projectId, deliveryId }) => ({
        url: `/projects/${projectId}/deliveries/${deliveryId}/in-transit`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { deliveryId }) => [
        'Delivery',
        { type: 'Delivery', id: deliveryId },
        'DeliveryStats',
      ],
    }),

    // =========================================================================
    // WORKFLOW: ARRIVE
    // =========================================================================

    markDeliveryArrived: builder.mutation<
      ApiResponse<Delivery>,
      { projectId: string; deliveryId: string; data: ArriveDeliveryRequest }
    >({
      query: ({ projectId, deliveryId, data }) => ({
        url: `/projects/${projectId}/deliveries/${deliveryId}/arrive`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { deliveryId }) => [
        'Delivery',
        { type: 'Delivery', id: deliveryId },
        'DeliveryStats',
      ],
    }),

    // =========================================================================
    // WORKFLOW: RECEIVE
    // =========================================================================

    receiveDelivery: builder.mutation<
      ApiResponse<Delivery>,
      { projectId: string; deliveryId: string; data: ReceiveDeliveryRequest }
    >({
      query: ({ projectId, deliveryId, data }) => ({
        url: `/projects/${projectId}/deliveries/${deliveryId}/receive`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { deliveryId }) => [
        'Delivery',
        { type: 'Delivery', id: deliveryId },
        'DeliveryStats',
      ],
    }),

    // =========================================================================
    // WORKFLOW: COMPLETE
    // =========================================================================

    completeDelivery: builder.mutation<
      ApiResponse<Delivery>,
      { projectId: string; deliveryId: string }
    >({
      query: ({ projectId, deliveryId }) => ({
        url: `/projects/${projectId}/deliveries/${deliveryId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { deliveryId }) => [
        'Delivery',
        { type: 'Delivery', id: deliveryId },
        'DeliveryStats',
      ],
    }),
  }),
});

export const {
  useGetDeliveriesQuery,
  useGetDeliveryStatsQuery,
  useGetDeliveryQuery,
  useCreateDeliveryMutation,
  useUpdateDeliveryMutation,
  useDeleteDeliveryMutation,
  useConfirmDeliveryMutation,
  useMarkDeliveryInTransitMutation,
  useMarkDeliveryArrivedMutation,
  useReceiveDeliveryMutation,
  useCompleteDeliveryMutation,
} = deliveryApi;
